import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, ArrowRight, ArrowLeft, Search, Building2, Users, FolderKanban, Receipt, TrendingUp } from "lucide-react";
import { api, apiErrorMessage } from "../../api";
import { useAuth } from "../../context/AuthContext";

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-lg border border-cream bg-white p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-md bg-forest text-white flex items-center justify-center">
        <Icon size={18} />
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-forest/50">{label}</div>
        <div className="text-lg font-semibold text-forest">{value}</div>
      </div>
    </div>
  );
}

export default function StaffConsole() {
  const nav = useNavigate();
  const { user, switchWorkspace, refreshMe } = useAuth();
  const [stats, setStats] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, ws] = await Promise.all([api.platformStats(), api.platformWorkspaces()]);
        setStats(s);
        setWorkspaces(ws);
      } catch (e) {
        setErr(apiErrorMessage(e, "Failed to load platform data"));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return workspaces;
    const needle = q.toLowerCase();
    return workspaces.filter(
      (w) =>
        w.name.toLowerCase().includes(needle) ||
        (w.owner_email || "").toLowerCase().includes(needle) ||
        (w.owner_name || "").toLowerCase().includes(needle)
    );
  }, [q, workspaces]);

  const enterWorkspace = async (id) => {
    // Ensure workspace is present in AuthContext (staff sees all via /me)
    await refreshMe();
    switchWorkspace(id);
    nav("/app");
  };

  if (loading) return <div className="p-8 text-forest/60">Loading Staff Console…</div>;
  if (err) return <div className="p-8 text-red-600" data-testid="staff-error">{err}</div>;

  return (
    <div className="min-h-screen bg-cream-2">
      <div className="bg-forest text-white">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex items-center gap-3">
            <Shield size={22} className="text-coral" />
            <div>
              <div className="text-xs uppercase tracking-wide text-white/50">The Bench — Platform</div>
              <h1 className="font-display text-3xl">Staff Console</h1>
            </div>
            <div className="ml-auto flex items-center gap-4">
              <div className="text-sm text-white/70 hidden sm:block" data-testid="staff-user">
                Signed in as <b>{user?.name}</b> ({user?.platform_role})
              </div>
              <button
                onClick={() => nav("/app")}
                data-testid="staff-return-dashboard"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-sm font-medium px-3 py-1.5 rounded-md transition"
                title="Return to your Full Bench dashboard"
              >
                <ArrowLeft size={14} /> Return to dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="staff-stats">
            <StatCard label="Workspaces" value={stats.workspace_count} icon={Building2} />
            <StatCard label="Users" value={stats.user_count} icon={Users} />
            <StatCard label="Projects" value={stats.project_count} icon={FolderKanban} />
            <StatCard label="Invoices" value={stats.invoice_count} icon={Receipt} />
            <StatCard
              label="GMV invoiced"
              value={`$${Math.round(stats.total_invoiced).toLocaleString()}`}
              icon={TrendingUp}
            />
          </div>
        )}

        <div className="bg-white rounded-lg border border-cream">
          <div className="px-4 py-3 border-b border-cream flex items-center gap-3">
            <div className="relative flex-1 max-w-md">
              <Search size={14} className="absolute left-3 top-2.5 text-forest/40" />
              <input
                data-testid="staff-workspace-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search workspaces or owners…"
                className="w-full pl-8 pr-3 py-1.5 rounded border border-cream text-sm"
              />
            </div>
            <div className="text-xs text-forest/50">{filtered.length} of {workspaces.length}</div>
          </div>

          <div className="divide-y divide-cream">
            {filtered.map((w) => (
              <div
                key={w.id}
                className="px-4 py-3 flex items-center gap-4 hover:bg-cream-2"
                data-testid={`ws-row-${w.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-forest truncate">{w.name}</div>
                  <div className="text-xs text-forest/60 truncate">
                    Owner: {w.owner_name || "—"} · {w.owner_email || "unknown"}
                  </div>
                </div>
                <div className="hidden md:flex gap-6 text-xs text-forest/70">
                  <div>
                    <div className="uppercase tracking-wide text-[10px] text-forest/40">Team</div>
                    <div>{w.member_count}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wide text-[10px] text-forest/40">Projects</div>
                    <div>{w.project_count}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wide text-[10px] text-forest/40">Open Inv.</div>
                    <div>{w.open_invoice_count}</div>
                  </div>
                  <div>
                    <div className="uppercase tracking-wide text-[10px] text-forest/40">Invoiced</div>
                    <div>${Math.round(w.total_invoiced).toLocaleString()}</div>
                  </div>
                </div>
                <button
                  onClick={() => enterWorkspace(w.id)}
                  className="btn-coral px-3 py-1.5 rounded text-sm flex items-center gap-1"
                  data-testid={`ws-enter-${w.id}`}
                >
                  Enter <ArrowRight size={14} />
                </button>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="px-4 py-8 text-center text-forest/50 text-sm">No matches</div>
            )}
          </div>
        </div>

        <div className="text-xs text-forest/50">
          Access as platform staff is invisible to clients. Actions are performed with a synthetic
          owner-equivalent role.
        </div>
      </div>
    </div>
  );
}
